import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { isApiError, programApi } from "../api";
import type { Schema } from "../types/generated";
import { useAuth } from "./useAuth";

export interface AcademyPageOptions {
  limit?: number;
  offset?: number;
  enabled?: boolean;
}

export const academyQueryKeys = {
  actor: (actor: string | null) => ["academy", actor] as const,
  space: (actor: string | null, programId: string) =>
    ["academy", actor, "space", programId] as const,
  retention: (actor: string | null, programId: string, enrollmentId?: string) =>
    ["academy", actor, "retention", programId, enrollmentId ?? null] as const,
  materials: (actor: string | null, programId: string, enrollmentId?: string) =>
    ["academy", actor, "materials", programId, enrollmentId ?? null] as const,
  assignments: (
    actor: string | null,
    programId: string,
    enrollmentId?: string,
  ) =>
    ["academy", actor, "assignments", programId, enrollmentId ?? null] as const,
  targets: (actor: string | null, programId: string, assignmentId: string) =>
    ["academy", actor, "targets", programId, assignmentId] as const,
  submissions: (actor: string | null, targetId: string, enrollmentId: string) =>
    ["academy", actor, "submissions", targetId, enrollmentId] as const,
};

export function useClassLearningSpace(
  programId: string | undefined,
): UseQueryResult<Schema["ProgramLearningSpaceResponse"], unknown> {
  const { user, loading } = useAuth();
  return useAcademyQuery(
    academyQueryKeys.space(user?.uid ?? null, programId ?? ""),
    () => programApi.getLearningSpace(programId as string),
    Boolean(user?.uid && programId && !loading),
  );
}

function useAcademyQuery<T>(
  key: readonly unknown[],
  read: (signal: AbortSignal) => Promise<T>,
  enabled: boolean,
): UseQueryResult<T, unknown> {
  const client = useQueryClient();
  const { user } = useAuth();
  const query = useQuery<T, unknown>({
    queryKey: key,
    queryFn: ({ signal }) => read(signal),
    enabled,
    retry: false,
    refetchOnMount: "always",
  });
  const accessLost =
    query.isError &&
    isApiError(query.error) &&
    [401, 403, 404].includes(query.error.status);
  useEffect(() => {
    if (!accessLost) return;
    client.removeQueries({
      queryKey: academyQueryKeys.actor(user?.uid ?? null),
      type: "inactive",
    });
  }, [accessLost, client, user?.uid]);
  return {
    ...query,
    data: query.isFetchedAfterMount && !query.isError ? query.data : undefined,
  } as UseQueryResult<T, unknown>;
}

export function useClassRetention(
  programId: string | undefined,
  enrollmentId?: string,
): UseQueryResult<Schema["ClassLearningRetentionPolicy"], unknown> {
  const { user, loading } = useAuth();
  return useAcademyQuery(
    academyQueryKeys.retention(
      user?.uid ?? null,
      programId ?? "",
      enrollmentId,
    ),
    (signal) =>
      programApi.getClassRetention(programId as string, enrollmentId, signal),
    Boolean(user?.uid && programId && !loading),
  );
}

export function useClassMaterials(
  programId: string | undefined,
  enrollmentId?: string,
  options: AcademyPageOptions = {},
): UseQueryResult<Schema["ClassMaterialPage"], unknown> {
  const { user, loading } = useAuth();
  return useAcademyQuery(
    [
      ...academyQueryKeys.materials(
        user?.uid ?? null,
        programId ?? "",
        enrollmentId,
      ),
      options.limit ?? null,
      options.offset ?? null,
    ],
    (signal) =>
      programApi.listClassMaterials(
        programId as string,
        {
          enrollment_id: enrollmentId,
          limit: options.limit,
          offset: options.offset,
        },
        signal,
      ),
    Boolean(user?.uid && programId && !loading && options.enabled !== false),
  );
}

export function useClassAssignments(
  programId: string | undefined,
  enrollmentId?: string,
  options: AcademyPageOptions = {},
): UseQueryResult<Schema["ClassAssignmentPage"], unknown> {
  const { user, loading } = useAuth();
  return useAcademyQuery(
    [
      ...academyQueryKeys.assignments(
        user?.uid ?? null,
        programId ?? "",
        enrollmentId,
      ),
      options.limit ?? null,
      options.offset ?? null,
    ],
    (signal) =>
      programApi.listClassAssignments(
        programId as string,
        {
          enrollment_id: enrollmentId,
          limit: options.limit,
          offset: options.offset,
        },
        signal,
      ),
    Boolean(user?.uid && programId && !loading && options.enabled !== false),
  );
}

export function useClassAssignmentTargets(
  programId: string | undefined,
  assignmentId: string | undefined,
  options: AcademyPageOptions = {},
): UseQueryResult<Schema["ClassAssignmentTargetPage"], unknown> {
  const { user, loading } = useAuth();
  return useAcademyQuery(
    [
      ...academyQueryKeys.targets(
        user?.uid ?? null,
        programId ?? "",
        assignmentId ?? "",
      ),
      options.limit ?? null,
      options.offset ?? null,
    ],
    (signal) =>
      programApi.listClassAssignmentTargets(
        programId as string,
        assignmentId as string,
        options,
        signal,
      ),
    Boolean(
      user?.uid &&
        programId &&
        assignmentId &&
        !loading &&
        options.enabled !== false,
    ),
  );
}

export function useClassSubmissions(
  targetId: string | undefined,
  enrollmentId: string | undefined,
  options: AcademyPageOptions = {},
): UseQueryResult<Schema["ClassSubmissionPage"], unknown> {
  const { user, loading } = useAuth();
  return useAcademyQuery(
    [
      ...academyQueryKeys.submissions(
        user?.uid ?? null,
        targetId ?? "",
        enrollmentId ?? "",
      ),
      options.limit ?? null,
      options.offset ?? null,
    ],
    (signal) =>
      programApi.listClassSubmissions(
        targetId as string,
        enrollmentId as string,
        options,
        signal,
      ),
    Boolean(
      user?.uid &&
        targetId &&
        enrollmentId &&
        !loading &&
        options.enabled !== false,
    ),
  );
}

function useAcademyMutation<TData, TVariables>(
  mutationKey: readonly unknown[],
  mutate: (variables: TVariables) => Promise<TData>,
): UseMutationResult<TData, unknown, TVariables> {
  const { user, loading } = useAuth();
  const client = useQueryClient();
  return useMutation<TData, unknown, TVariables>({
    mutationKey: [...academyQueryKeys.actor(user?.uid ?? null), ...mutationKey],
    retry: false,
    mutationFn: (variables) => {
      if (!user?.uid || loading)
        throw new Error("Sign in before changing Academy content.");
      return mutate(variables);
    },
    onSuccess: async () => {
      await client.invalidateQueries({
        queryKey: academyQueryKeys.actor(user?.uid ?? null),
      });
    },
    onError: (error) => {
      if (isApiError(error) && [401, 403, 404].includes(error.status)) {
        client.removeQueries({
          queryKey: academyQueryKeys.actor(user?.uid ?? null),
        });
      }
    },
  });
}

export function useConfigureClassRetention(programId: string) {
  return useAcademyMutation(
    ["retention", programId, "configure"],
    (body: Schema["ClassLearningRetentionPolicyWrite"]) =>
      programApi.configureClassRetention(programId, body),
  );
}

export function useSaveClassMaterial(programId: string) {
  return useAcademyMutation(
    ["materials", programId, "save"],
    (input: { materialId?: string; body: Schema["ClassMaterialWrite"] }) =>
      input.materialId
        ? programApi.updateClassMaterial(
            programId,
            input.materialId,
            input.body,
          )
        : programApi.createClassMaterial(programId, input.body),
  );
}

export function useTransitionClassMaterial(programId: string) {
  return useAcademyMutation(
    ["materials", programId, "transition"],
    (input: {
      materialId: string;
      transition: "publish" | "archive";
      body: Schema["ClassSubmissionCommand"];
    }) =>
      programApi.transitionClassMaterial(
        programId,
        input.materialId,
        input.transition,
        input.body,
      ),
  );
}

export function useSaveClassAssignment(programId: string) {
  return useAcademyMutation(
    ["assignments", programId, "save"],
    (input: { assignmentId?: string; body: Schema["ClassAssignmentWrite"] }) =>
      input.assignmentId
        ? programApi.updateClassAssignment(
            programId,
            input.assignmentId,
            input.body,
          )
        : programApi.createClassAssignment(programId, input.body),
  );
}

export function usePublishClassAssignment(programId: string) {
  return useAcademyMutation(
    ["assignments", programId, "publish"],
    (input: { assignmentId: string; body: Schema["ClassAssignmentPublish"] }) =>
      programApi.publishClassAssignment(
        programId,
        input.assignmentId,
        input.body,
      ),
  );
}

export function useSaveClassSubmission(targetId: string, enrollmentId: string) {
  return useAcademyMutation(
    ["submissions", targetId, "save"],
    (body: Schema["ClassSubmissionWrite"]) =>
      programApi.saveClassSubmission(targetId, enrollmentId, body),
  );
}

export function useSubmitClassSubmission(
  targetId: string,
  enrollmentId: string,
) {
  return useAcademyMutation(
    ["submissions", targetId, "submit"],
    (input: { submissionId: string; body: Schema["ClassSubmissionCommand"] }) =>
      programApi.submitClassSubmission(
        targetId,
        enrollmentId,
        input.submissionId,
        input.body,
      ),
  );
}

export function useReviewClassSubmission(programId: string) {
  return useAcademyMutation(
    ["submissions", programId, "review"],
    (input: {
      submissionId: string;
      body: Schema["ClassSubmissionReviewWrite"];
    }) =>
      programApi.reviewClassSubmission(
        programId,
        input.submissionId,
        input.body,
      ),
  );
}

export function useUploadClassFile(programId: string) {
  return useAcademyMutation(
    ["files", programId, "upload"],
    async (input: { reserve: Schema["ClassFileReserve"]; file: File }) => {
      const reservation = await programApi.reserveClassFile(
        programId,
        input.reserve,
      );
      return programApi.uploadClassFile(reservation.file_id, input.file);
    },
  );
}
import { useEffect } from "react";

export function useRemoveClassFile(programId: string) {
  return useAcademyMutation(
    ["files", programId, "remove"],
    (input: { fileId: string; expectedRevision: number }) =>
      programApi.removeClassFile(input.fileId, {
        expected_revision: input.expectedRevision,
        idempotency_key: crypto.randomUUID(),
      }),
  );
}
